import { openDB, type DBSchema, type IDBPDatabase } from 'idb';

/**
 * A mutation that has been applied optimistically but not yet confirmed by the server.
 *
 * Only the *intent* is stored, never a serialised HTTP request: replaying a stored
 * request would send a stale bearer token, and a queue built in a supermarket dead spot
 * is very often flushed after the token has been refreshed.
 */
export type QueuedMutation =
  | { kind: 'add'; listId: string; itemId: string; name: string; note?: string }
  | { kind: 'check'; listId: string; itemId: string }
  | { kind: 'uncheck'; listId: string; itemId: string }
  // Assignment rides along with the edit that changed it rather than being its own
  // intent: they are saved together, and two queued entries would make a rename and a
  // handover replay as two separate changes on everyone else's screen.
  | {
      kind: 'update';
      listId: string;
      itemId: string;
      name: string;
      note?: string | null;
      assignedToUserId?: string | null;
    }
  | { kind: 'delete'; listId: string; itemId: string }
  | { kind: 'clearChecked'; listId: string }
  // The compressed bytes travel with the intent. IndexedDB structure-clones a Blob
  // natively, so this is still what the user *meant* rather than a serialised request —
  // no base64, no size inflation, and no stale bearer token baked into it.
  //
  // The image id is minted when the photo is taken, not when it is sent, so a replay
  // lands on the same row instead of orphaning the first id under a URL the browser has
  // already cached.
  | {
      kind: 'attachImage';
      listId: string;
      itemId: string;
      imageId: string;
      blob: Blob;
    }
  | { kind: 'removeImage'; listId: string; itemId: string }
  // Its own intent rather than a field on `update`, because it is a different endpoint and
  // a different key space: an aisle is filed against the *name*, and the update is about
  // the row. Folding it in would mean a rename and a correction replayed as one request
  // that the server has no way to honour.
  | { kind: 'setAisle'; listId: string; itemId: string; aisle: string }
  | { kind: 'clearAisle'; listId: string; itemId: string };

export interface QueueEntry {
  /** Auto-incrementing, and therefore the flush order. */
  id?: number;
  mutation: QueuedMutation;
  queuedAt: number;
  /** Failed attempts so far, so a permanently broken entry can be given up on. */
  attempts: number;
}

interface OfflineDb extends DBSchema {
  mutations: {
    key: number;
    value: QueueEntry;
    indexes: { 'by-list': string };
  };
}

const DB_NAME = 'bitsandbobs';

/**
 * Deliberately still 1, and adding a variant to `QueuedMutation` must not change it.
 *
 * A new kind of intent is not a schema change: no new store, no new index, no changed
 * key path. And `upgrade` below creates the store unconditionally, so opening at version
 * 2 would run it against a database that already has one — a `ConstraintError`, and a
 * dead queue for every installation that has ever run the app. If a bump is ever
 * genuinely needed, `upgrade` has to branch on `oldVersion` first.
 */
const DB_VERSION = 1;

let dbPromise: Promise<IDBPDatabase<OfflineDb>> | null = null;

function db(): Promise<IDBPDatabase<OfflineDb>> {
  dbPromise ??= openDB<OfflineDb>(DB_NAME, DB_VERSION, {
    upgrade(database) {
      const store = database.createObjectStore('mutations', {
        keyPath: 'id',
        // Monotonic keys are what make the queue an ordered log: replaying "add milk"
        // then "check milk" in the other order would fail.
        autoIncrement: true,
      });

      store.createIndex('by-list', 'mutation.listId');
    },
  });

  return dbPromise;
}

/**
 * Told whenever something joins the queue.
 *
 * Queuing has to be an *event*, because the queue is on screen: the unsent counter, and
 * since `applyPending` the rows themselves. It used to be something each mutation site
 * remembered to announce, with a zero-delay timer fired at the moment of the tap — which
 * raced the request whose failure does the enqueuing, and won every time. The screen was
 * therefore permanently one intent behind: the counter lagged, and an item added offline
 * needed two taps to go into the trolley, because the first tap's intent was only read
 * during the second.
 *
 * Deliberately not fired when an entry is *removed*. A flush takes rows out of the queue
 * before it reconciles, and a row drawn only from the queue would blink out and back;
 * `queueStore` publishes the drained queue once, after the refetch it triggers has landed.
 */
const changeListeners = new Set<() => void>();

export function onQueueChanged(listener: () => void): () => void {
  changeListeners.add(listener);

  return () => {
    changeListeners.delete(listener);
  };
}

function announce(): void {
  for (const listener of changeListeners) {
    listener();
  }
}

/**
 * Which queued intents a later one makes pointless.
 *
 * Within a family, for one item, only the last intent means anything: the second `check`
 * of a row says nothing the first did not, and an `update` carries the whole row, so an
 * earlier one is entirely overwritten. Toggling a row back and forth in a shop otherwise
 * appended an entry per tap, and the unsent counter climbed past the number of things the
 * person had actually touched — which is worrying rather than informative, at the exact
 * moment the only thing that number is for is reassurance.
 *
 * `add`, `delete` and `clearChecked` are in no family and are never collapsed. An add is
 * the only record that a row exists at all, and a delete after an add is two facts about
 * two different moments — folding either away would mean deciding, from the client, that
 * something the server has never heard of does not need telling.
 */
type Family = 'checked' | 'fields' | 'aisle' | 'photo';

const FAMILY: Record<QueuedMutation['kind'], Family | null> = {
  add: null,
  delete: null,
  clearChecked: null,
  check: 'checked',
  uncheck: 'checked',
  update: 'fields',
  setAisle: 'aisle',
  clearAisle: 'aisle',
  attachImage: 'photo',
  removeImage: 'photo',
};

function supersedes(next: QueuedMutation, existing: QueuedMutation): boolean {
  const family = FAMILY[next.kind];

  if (family === null || family !== FAMILY[existing.kind]) {
    return false;
  }

  return 'itemId' in next && 'itemId' in existing && next.itemId === existing.itemId;
}

/**
 * Records an intent, dropping any it supersedes.
 *
 * The new entry always goes on the *end*, even when it replaces one from the beginning.
 * That is what makes this safe against a flush that is already in the air: if the entry
 * being dropped has in fact just been sent, the one appended here still corrects it. The
 * cheaper-looking alternative — cancelling a check against its uncheck and leaving nothing
 * — would throw the correction away in exactly that case.
 *
 * Appending can only move an intent later, never earlier, and the only thing it can move
 * past for the same row is a `clearChecked`, which no one can have reached the superseded
 * row through: clearing takes it off the screen.
 */
export async function enqueue(mutation: QueuedMutation): Promise<number> {
  const database = await db();
  const transaction = database.transaction('mutations', 'readwrite');

  for (const existing of await transaction.store.getAll()) {
    if (existing.id !== undefined && supersedes(mutation, existing.mutation)) {
      await transaction.store.delete(existing.id);
    }
  }

  const id = await transaction.store.add({
    mutation,
    queuedAt: Date.now(),
    attempts: 0,
  });

  await transaction.done;
  announce();

  return id;
}

/** Everything waiting, oldest first. */
export async function pending(): Promise<QueueEntry[]> {
  const database = await db();
  const entries = await database.getAll('mutations');

  return entries.sort((a, b) => (a.id ?? 0) - (b.id ?? 0));
}

export async function pendingCount(): Promise<number> {
  const database = await db();

  return database.count('mutations');
}

export async function remove(id: number): Promise<void> {
  const database = await db();
  await database.delete('mutations', id);
}

export async function recordFailure(entry: QueueEntry): Promise<void> {
  if (entry.id === undefined) {
    return;
  }

  const database = await db();
  await database.put('mutations', { ...entry, attempts: entry.attempts + 1 });
}

export async function clear(): Promise<void> {
  const database = await db();
  await database.clear('mutations');
  announce();
}

/**
 * Item ids that have a queued mutation. The UI dims these so it is visible that a change
 * has not yet reached the server — quietly showing them as settled would be a lie.
 */
export async function pendingItemIds(): Promise<Set<string>> {
  const entries = await pending();

  return new Set(
    entries
      .map((entry) => ('itemId' in entry.mutation ? entry.mutation.itemId : null))
      .filter((id): id is string => id !== null),
  );
}
